import { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon, Star } from "lucide-react";
import api from "../../lib/api";

async function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Upload multi-images pour un événement :
 * - Une image principale (banner) mise en avant
 * - Une galerie d'images secondaires
 * - Upload via Cloudinary si configuré, sinon fallback base64
 */
export default function EventImagesUpload({ banner, onBanner, images, onImages }) {
  const [uploading, setUploading] = useState(false);

  const uploadOne = async (file, folder) => {
    const data = await fileToDataURL(file);
    const r = await api.post("/admin/upload/image", { data, folder });
    return r.data.url;
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const url = await uploadOne(file, "frontunirdc/events/banners");
      onBanner(url);
      toast.success("Bannière téléversée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'upload");
    } finally { setUploading(false); }
  };

  const uploadGallery = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of arr) {
        const url = await uploadOne(file, "frontunirdc/events/gallery");
        uploaded.push(url);
      }
      onImages([...(images || []), ...uploaded]);
      toast.success(`${uploaded.length} image(s) ajoutée(s)`);
    } catch (e) {
      toast.error("Échec de l'upload");
    } finally { setUploading(false); }
  };

  const removeImage = (idx) => onImages(images.filter((_, i) => i !== idx));
  const promoteToBanner = (idx) => {
    // Fait passer une image de la galerie en bannière (et déplace l'ancienne bannière en galerie)
    const url = images[idx];
    const newImages = [...images];
    newImages.splice(idx, 1);
    if (banner) newImages.unshift(banner);
    onBanner(url);
    onImages(newImages);
    toast.success("Image définie comme bannière");
  };

  return (
    <div className="pt-3 border-t border-border space-y-4" data-testid="event-images-upload">
      <div className="eyebrow text-foreground/60">Images de l'événement</div>

      {/* Bannière principale */}
      <div>
        <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-2">
          Image principale (bannière)
        </label>
        <div className="grid grid-cols-[180px,1fr] gap-3 items-start">
          <div className="aspect-[16/10] rounded-sm bg-ink/5 border border-dashed border-border overflow-hidden grid place-items-center relative">
            {banner ? (
              <img src={banner} alt="bannière" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="w-8 h-8 text-foreground/30" />
            )}
            {banner && (
              <div className="absolute top-1 left-1 inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider bg-gold text-ink px-1.5 py-0.5 rounded font-bold">
                <Star className="w-2.5 h-2.5" /> Bannière
              </div>
            )}
            {uploading && <div className="absolute inset-0 bg-ink/60 grid place-items-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>}
          </div>
          <div className="space-y-2">
            <label className="btn-outline text-xs cursor-pointer inline-flex" data-testid="event-banner-upload">
              <Upload className="w-3.5 h-3.5" />
              <span>Téléverser bannière</span>
              <input type="file" accept="image/*" hidden onChange={(e) => uploadBanner(e.target.files?.[0])} />
            </label>
            <div className="text-[0.7rem] text-foreground/55">
              Format recommandé : paysage 16/10, JPG/PNG. Cette image apparaîtra dans la rubrique Événements passés et sur la page dédiée.
            </div>
            <input value={banner || ""} onChange={(e) => onBanner(e.target.value)}
                   className="inp text-xs" placeholder="…ou URL directe" data-testid="event-banner-url" />
            {banner && (
              <button type="button" onClick={() => onBanner("")}
                      className="text-xs text-crimson hover:underline inline-flex items-center gap-1" data-testid="event-banner-clear">
                <X className="w-3 h-3" /> Retirer la bannière
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Galerie */}
      <div>
        <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-2">
          Galerie photos additionnelles ({(images || []).length})
        </label>
        <label className="btn-outline text-xs cursor-pointer inline-flex" data-testid="event-gallery-upload">
          <Upload className="w-3.5 h-3.5" />
          <span>Ajouter des images</span>
          <input type="file" accept="image/*" multiple hidden onChange={(e) => uploadGallery(e.target.files)} />
        </label>

        {(images || []).length > 0 && (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
            {images.map((url, i) => (
              <div key={i} className="relative aspect-square rounded-sm overflow-hidden bg-ink/5 group border border-border" data-testid={`event-gallery-${i}`}>
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center gap-1">
                  <button type="button" onClick={() => promoteToBanner(i)}
                          className="bg-gold text-ink text-[0.65rem] px-2 py-1 rounded inline-flex items-center gap-1 font-semibold"
                          title="Utiliser comme bannière" data-testid={`event-gallery-promote-${i}`}>
                    <Star className="w-3 h-3" /> Bannière
                  </button>
                  <button type="button" onClick={() => removeImage(i)}
                          className="bg-crimson text-white text-[0.65rem] px-2 py-1 rounded inline-flex items-center gap-1"
                          title="Retirer" data-testid={`event-gallery-remove-${i}`}>
                    <X className="w-3 h-3" /> Retirer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-1 text-[0.7rem] text-foreground/55">
          Cliquez sur une image de la galerie pour la définir comme bannière ou la retirer.
        </div>
      </div>
    </div>
  );
}
